import React, {useCallback, useEffect, useState} from "react";
import {Alert, Box, Button, Card, CardContent, CircularProgress, Typography} from "@mui/material";
import {Add as AddIcon, LocalOffer as CouponIcon} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import {useUser} from "../../providers/user-context";
import ConfirmDeleteDialog from "../../dialog/coupon/confirm-delete-dialog";
import PharmacyListDialog from "../../dialog/coupon/pharmacy-list-dialog";
import CouponFormDialog from "../../dialog/coupon/coupon-form-dialog";
import {couponForm, emptyCouponForm, validCouponForm} from "./coupon-api";
import useCouponManagement from "./use-coupon-management";
import CouponCampaignList from "./coupon-campaign-list";
import CouponCampaignFilters from "./coupon-campaign-filters";
import useCouponClock from "./use-coupon-clock";
import {filterCoupons} from "./coupon-lifecycle";

function CouponPanel({createRequest, onWorkflowOpened}) {
    const {t, i18n} = useTranslation();
    const manager = useCouponManagement(i18n.language);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingCoupon, setEditingCoupon] = useState(null);
    const [formData, setFormData] = useState(emptyCouponForm);
    const [formError, setFormError] = useState("");
    const [couponToDelete, setCouponToDelete] = useState(null);
    const [recipientCoupon, setRecipientCoupon] = useState(null);
    const {coupons, busy, loading, loadError, notice} = manager;
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("ALL");
    const now = useCouponClock(coupons);
    const visible = filterCoupons(coupons || [], search, status, now);
    const error = notice?.severity === "error" ? t(notice.message) : "";

    const {clearNotice} = manager;
    const openForm = useCallback((coupon = null) => {
        if (busy) return;
        clearNotice();
        setEditingCoupon(coupon);
        setFormData(coupon ? couponForm(coupon) : emptyCouponForm());
        setFormError("");
        setOpenDialog(true);
    }, [busy, clearNotice]);
    useEffect(() => {
        if (createRequest && !loading && coupons !== null && !busy) {
            openForm();
            onWorkflowOpened?.();
        }
    }, [createRequest, loading, coupons, busy, openForm, onWorkflowOpened]);
    const closeForm = () => { if (!busy) setOpenDialog(false); };
    const save = async () => {
        if (!validCouponForm(formData, editingCoupon)) { setFormError(t("CouponInvalidForm")); return; }
        if (await manager.save(formData, editingCoupon)) setOpenDialog(false);
    };
    const confirmDelete = async () => {
        if (couponToDelete && await manager.remove(couponToDelete)) setCouponToDelete(null);
    };

    return <Box>
        <Box sx={{display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", mb: 3, gap: 2}}>
            <Typography variant="h6">{t("Coupons")} ({loading || loadError ? "—" : coupons?.length ?? "—"})</Typography>
            <Box sx={{display: "flex", flexWrap: "wrap", gap: 1}}>
                <Button onClick={manager.reload} disabled={!!busy || loading}>{t("Refresh")}</Button>
                <Button variant="contained" startIcon={<AddIcon/>} disabled={!!busy || loading || coupons === null}
                    onClick={() => openForm()}>{t("CreateNewCoupon")}</Button>
            </Box>
        </Box>
        {loadError && <Alert severity="error" sx={{mb: 2}} action={<Button onClick={manager.reload} disabled={!!busy || loading}>
            {t("Retry")}</Button>}>{t(loadError)}</Alert>}
        {notice && !openDialog && !couponToDelete && <Alert severity={notice.severity}
            role={notice.severity === "success" ? "status" : "alert"} sx={{mb: 2}}>{t(notice.message)}</Alert>}
        {!loading && !loadError && coupons?.length > 0 && <CouponCampaignFilters coupons={coupons} visible={visible.length} now={now}
            search={search} status={status} setSearch={setSearch} setStatus={setStatus} t={t}/>}
        {loading ? <Box display="flex" justifyContent="center" py={6}><CircularProgress aria-label={t("Loading")}/></Box>
            : loadError ? null
            : coupons?.length === 0 ? <Card><CardContent sx={{textAlign: "center", py: 6}}>
                <CouponIcon sx={{fontSize: 64, color: "text.secondary", mb: 2}}/>
                <Typography variant="h6" gutterBottom>{t("NoCouponsCreatedYet")}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>{t("CreateFirstCouponDesc")}</Typography>
                <Button variant="contained" disabled={!!busy} onClick={() => openForm()}>{t("CreateFirstCoupon")}</Button>
            </CardContent></Card> : coupons && (visible.length === 0
                ? <Card><CardContent sx={{textAlign: "center", py: 4}}><Typography variant="h6">{t("CouponNoMatches")}</Typography>
                    <Typography variant="body2" color="text.secondary">{t("CouponNoMatchesHelp")}</Typography></CardContent></Card>
                : <CouponCampaignList coupons={visible} now={now} t={t} language={i18n.language} busy={busy}
                    onEdit={openForm} onToggle={manager.toggle} onRecipients={setRecipientCoupon}
                    onDelete={coupon => { manager.clearNotice(); setCouponToDelete(coupon); }}/>)}
        {openDialog && <CouponFormDialog open onClose={closeForm} onSave={save} saving={busy === "save"}
            error={formError || error} editingCoupon={editingCoupon} formData={formData}
            onInputChange={(field, value) => { setFormError(""); setFormData(previous => ({...previous, [field]: value})); }}/>}
        {couponToDelete && <ConfirmDeleteDialog open onClose={() => { if (!busy) setCouponToDelete(null); }}
            onConfirm={confirmDelete} saving={busy === "delete"} error={error} couponCode={couponToDelete.couponCode}/>}
        <PharmacyListDialog open={recipientCoupon !== null} onClose={() => setRecipientCoupon(null)}
            couponCode={recipientCoupon?.couponCode} pharmacies={recipientCoupon?.pharmacies || []}/>
    </Box>;
}

export default function TabMyCoupons({tabValue, createRequest = 0, onWorkflowOpened}) {
    const {tenantId, userIdentity} = useUser();
    const {i18n} = useTranslation();
    // A fresh panel fences pending requests and draft dialogs when the authenticated identity or tab changes.
    return <div role="tabpanel" hidden={tabValue !== 4} id="distributor-tabpanel-4" aria-labelledby="distributor-tab-4">
        {tabValue === 4 && tenantId && <CouponPanel key={`${tenantId}:${userIdentity}:${i18n.language}`}
            createRequest={createRequest} onWorkflowOpened={onWorkflowOpened}/>}
    </div>;
}
